import { useState, type ReactNode } from 'react';
import { Alert, Button, Card, CardBody, SelectField, TextField } from '@dru/ui';
import { api } from '../lib/api';
import { useQuery } from '../lib/useQuery';
import { errorMessage } from '../lib/auth';

type Preference = { mode: 'EXISTING' | 'AUTOMATIC' | null; prefix: string | null };

/** Appears only at employee entry, until the company makes its first choice. */
export function EmployeeIdChoice({ children }: { children: ReactNode }) {
  const preference = useQuery<Preference>(() => api.get('/employees/id-preference'), []);
  const [mode, setMode] = useState('EXISTING');
  const [prefix, setPrefix] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (preference.loading) return <p>Loading employee ID preference…</p>;
  if (preference.error) return <Alert tone="danger">{preference.error}<Button onClick={preference.reload}>Retry</Button></Alert>;
  if (preference.data?.mode) return <>{children}</>;
  async function save() {
    setBusy(true); setError(null);
    try {
      await api.post('/employees/id-preference', { mode, ...(mode === 'AUTOMATIC' ? { prefix } : {}) });
      preference.reload();
    } catch (e) { setError(errorMessage(e)); }
    finally { setBusy(false); }
  }
  return <Card><CardBody className="space-y-4">
    <h1 className="text-h3">How should employee IDs work?</h1>
    <p>Choose once for your company. Existing employee IDs are always preserved.</p>
    <SelectField label="Employee IDs" value={mode} onChange={setMode}>
      <option value="EXISTING">Use our existing employee IDs</option>
      <option value="AUTOMATIC">Generate IDs automatically</option>
    </SelectField>
    {mode === 'AUTOMATIC' && <TextField label="Employee ID prefix" value={prefix}
      onChange={v => setPrefix(v.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8))}
      hint="2 to 8 capital letters or digits, for example ACME. IDs will look like ACME-000001." />}
    {error && <Alert tone="danger">{error}</Alert>}
    <Button onClick={save} loading={busy} disabled={mode === 'AUTOMATIC' && !/^[A-Z0-9]{2,8}$/.test(prefix)}>Save and continue</Button>
  </CardBody></Card>;
}
